import { Box, Button, Flex, Text, useFormControlContext, useToast, VisuallyHidden } from "@chakra-ui/react";
import FileHandler from "@tiptap/extension-file-handler";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { TextStyleKit } from "@tiptap/extension-text-style";
import type { Content } from "@tiptap/react";
import { Editor, EditorContent, ReactNodeViewRenderer, useEditor } from "@tiptap/react";
import { Placeholder } from "@tiptap/extensions";
import { EditorState } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import { debounce } from "lodash";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { UploadStatusProvider } from "./UploadStatusContext";
import { ImageUploadStatus } from "./uploadStatus";
import { extractImageUploadIds } from "./imageNodes";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { ProjectDocumentDto, UploadDto, ViewDocumentAsOwnerDto } from "../../../autogen/bff-api";
import { useDocumentDownloader } from "../../documents/DownloadDocumentIconButton/useDocumentDownloader";
import { getLastSavedLabel } from "../../formatting/getLastSavedLabel";
import { useKeyboardModality } from "../../hooks/useKeyboardModality";
import { useKeyboardScrollable } from "../../hooks/useKeyboardScrollable";
import { UnsavedChangesError } from "../../editing/save-coordinator/UnsavedChangesError";
import { usePendingSaves } from "../pendingSaves";
import { KEYBOARD_SCROLL_FOCUS } from "../../KeyboardScrollBox";
import { EditorKeyboardHint, hasEditorKeyboardHint, isToolbarShortcut, toolbarShortcut } from "./EditorKeyboardHint";
import { type AskFurtherAbout, MenuBar, type MenuBarHandle, type ToolbarConfig } from "./MenuBar";
import { focusWithinHandlers } from "./useRovingToolbar";
import { MAX_IMAGE_WIDTH, ResizableImage } from "./ResizableImage";
import { uploadImageToS3 } from "./uploadImageToS3";
import {
  carriesUploadableImage,
  isExternalImageOnlyPaste,
  stripExternalImages,
  UPLOADABLE_IMAGE_TYPES,
} from "./externalImages";
import { requireStringEnvVar } from "../../../config";
import "./TipTap.scss";

const ownImageOrigins = (): string[] => {
  const origins = [window.location.origin];
  try {
    const uploads = requireStringEnvVar("VITE_S3_BASE_URL");
    if (uploads) origins.push(new URL(uploads).origin);
  } catch {
    return origins;
  }
  return origins;
};

export type TipTapDocument = ViewDocumentAsOwnerDto | ProjectDocumentDto;

export type TipTapCreatePostRequest = (params: { uploadId: string; file: File }) => Promise<UploadDto>;

export type TipTapCopyTemplateDocumentsResult = {
  uploadIdMapping: Record<string, string>; // old fromUploadId → new uploadId
  srcMapping: Record<string, string>; // old fromUploadId → blob URL for preview
};

export type TipTapCopyTemplateDocumentsHandler = (params: {
  templateDocuments: ViewDocumentAsOwnerDto[];
}) => Promise<TipTapCopyTemplateDocumentsResult>;

export type TipTapDocumentRemoveHandler = (params: { documentId: string }) => void | Promise<void>;

type HandleFileUpload = (params: { editor: Editor; files: File[]; position?: number }) => void;

const ImageWithUploadId = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      uploadId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-upload-id"),
        renderHTML: (attributes) => (attributes.uploadId ? { "data-upload-id": attributes.uploadId as string } : {}),
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width") || element.style.width?.replace("px", "") || null,
        renderHTML: (attributes) => (attributes.width ? { width: attributes.width as string } : {}),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height") || element.style.height?.replace("px", "") || null,
        renderHTML: (attributes) => (attributes.height ? { height: attributes.height as string } : {}),
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImage);
  },
});

const getImageDimensions = (src: string): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    const img = document.createElement("img");
    img.onload = () => {
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;

      // Cap dimensions to max width while preserving aspect ratio
      if (naturalWidth > MAX_IMAGE_WIDTH) {
        const scale = MAX_IMAGE_WIDTH / naturalWidth;
        resolve({
          width: Math.round(naturalWidth * scale),
          height: Math.round(naturalHeight * scale),
        });
      } else {
        resolve({ width: naturalWidth, height: naturalHeight });
      }
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = src;
  });
};

export { uploadImageToS3 } from "./uploadImageToS3";

const insertImageNode = ({
  editor,
  src,
  uploadId,
  position,
  width,
  height,
}: {
  editor: Editor;
  src: string | ArrayBuffer | null;
  uploadId?: string;
  position?: number;
  width?: number;
  height?: number;
}) => {
  editor
    .chain()
    .insertContentAt(position ?? editor.state.selection.anchor, {
      type: "image",
      attrs: {
        src,
        uploadId,
        width,
        height,
      },
    })
    .focus()
    .run();
};

const removeImageNode = ({ editor, uploadId }: { editor: Editor; uploadId: string }) => {
  const transaction = editor.state.tr;

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.uploadId === uploadId) {
      transaction.delete(pos, pos + node.nodeSize);
    }
  });

  if (transaction.docChanged) {
    editor.view.dispatch(transaction);
  }
};

const updateImageNodeByUploadId = ({ editor, uploadId, src }: { editor: Editor; uploadId: string; src: string }) => {
  const transaction = editor.state.tr;
  let foundUpdates = false;

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.uploadId === uploadId && node.attrs.src !== src) {
      transaction.setNodeMarkup(pos, undefined, { ...node.attrs, src });
      foundUpdates = true;
    }
  });

  if (foundUpdates && transaction.docChanged) {
    editor.view.dispatch(transaction.setMeta("addToHistory", false));
  }

  return foundUpdates;
};

const insertImageFromReader = ({ editor, file, position }: { editor: Editor; file: File; position?: number }) => {
  const fileReader = new FileReader();

  fileReader.readAsDataURL(file);
  fileReader.onload = () => {
    if (typeof fileReader.result === "string" || fileReader.result instanceof String) {
      insertImageNode({ editor, src: fileReader.result, position });
    }
  };
};

const createTipTapExtensions = (handleFileUpload: HandleFileUpload) => [
  StarterKit.configure({
    link: {
      openOnClick: true,
      autolink: true,
      linkOnPaste: true,
      defaultProtocol: "https",
    },
  }),
  Highlight.configure({ multicolor: true }),
  TableKit.configure({
    table: {
      resizable: true,
    },
  }),
  TextStyleKit.configure({}),
  ImageWithUploadId,
  FileHandler.configure({
    allowedMimeTypes: UPLOADABLE_IMAGE_TYPES,
    onDrop: (currentEditor, files, pos) =>
      handleFileUpload({
        editor: currentEditor,
        files,
        position: pos,
      }),
    onPaste: (currentEditor, files, htmlContent) => {
      if (htmlContent && !isExternalImageOnlyPaste(htmlContent, ownImageOrigins())) {
        return false;
      }

      handleFileUpload({
        editor: currentEditor,
        files,
        position: currentEditor.state.selection.anchor,
      });

      return true;
    },
  }),
];

const defaultHandleFileUpload: HandleFileUpload = ({ editor, files, position }) => {
  files.forEach((file) => {
    insertImageFromReader({ editor, file, position });
  });
};

export const extensions = createTipTapExtensions(defaultHandleFileUpload);

export const parseTiptapContent = (value?: string) => {
  try {
    const parsed = JSON.parse(value ?? "");
    return parsed;
  } catch (e) {
    return value;
  }
};

// True when the content carries no rendered text or media — covers an empty
// string, a plain whitespace string, and the "empty doc" JSON the editor emits
// after a field is typed into and then cleared ({type:"doc",content:[{type:"paragraph"}]}).
export const isTiptapContentEmpty = (value?: string | null): boolean => {
  if (!value) return true;
  const parsed = parseTiptapContent(value);
  if (typeof parsed === "string") return parsed.trim().length === 0;

  let hasContent = false;
  const visit = (node: unknown) => {
    if (hasContent || node == null || typeof node !== "object") return;
    const n = node as { type?: string; text?: string; content?: unknown[] };
    if (typeof n.text === "string" && n.text.trim().length > 0) {
      hasContent = true;
      return;
    }
    if (n.type === "image") {
      hasContent = true;
      return;
    }
    if (Array.isArray(n.content)) n.content.forEach(visit);
  };
  visit(parsed);
  return !hasContent;
};


export const TipTap = ({
  content,
  editable = true,
  onUpdate,
  onDocumentRemove,
  hasBorder = true,
  padding = "2",
  maxHeight = "96",
  createPostRequest,
  documents,
  debounceWait = 15000,
  debounceMaxWait = 30000,
  toolbar,
  onAutofill,
  isOpeningAutofill,
  askFurtherAbout,
  onEditorReady,
  textDocumentId,
  ariaLabel,
  placeholder,
  savesOnFlush = false,
}: {
  content: string;
  editable?: boolean;
  onUpdate?: (value: string) => void | Promise<void>;
  onDocumentRemove?: TipTapDocumentRemoveHandler;
  hasBorder?: boolean;
  padding?: string;
  maxHeight?: string;
  createPostRequest?: TipTapCreatePostRequest;
  documents?: TipTapDocument[];
  debounceWait?: number;
  debounceMaxWait?: number;
  toolbar?: ToolbarConfig;
  onAutofill?: () => void;
  isOpeningAutofill?: boolean;
  askFurtherAbout?: AskFurtherAbout;
  onEditorReady?: (editor: Editor) => void;
  textDocumentId?: string;
  ariaLabel?: string;
  placeholder?: string;
  savesOnFlush?: boolean;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { getDownloadLink } = useDocumentDownloader();

  const [statusByUploadId, setStatusByUploadId] = useState<Map<string, ImageUploadStatus>>(new Map());
  const setImageStatus = useCallback((uploadId: string, status: ImageUploadStatus) => {
    setStatusByUploadId((prev) => new Map(prev).set(uploadId, status));
  }, []);

  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [lastSavedLabel, setLastSavedLabel] = useState<string | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const previousUploadIdsRef = useRef<Set<string>>(new Set());
  const removedUploadIdsRef = useRef<Set<string>>(new Set());
  const savedUploadIdsRef = useRef<Set<string>>(new Set());
  const latestSaveRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const removeDocumentRef = useRef({ onDocumentRemove, documents });
  removeDocumentRef.current = { onDocumentRemove, documents };
  const onUpdateRef = useRef(onUpdate);
  const menuBarRef = useRef<MenuBarHandle>(null);
  const incomingImageFileRef = useRef(false);
  const placeholderRef = useRef(placeholder ?? "");
  placeholderRef.current = placeholder ?? "";

  const keyboardHintId = useId();
  const formControl = useFormControlContext();
  const readOnlyScroll = useKeyboardScrollable<HTMLDivElement>(!editable, { evenWithFocusableContent: true });
  const isKeyboardModality = useKeyboardModality();
  const [editorFocused, setEditorFocused] = useState(false);
  const [enteredByKeyboard, setEnteredByKeyboard] = useState(false);
  const [toolbarFocused, setToolbarFocused] = useState(false);

  // Keep the ref up to date without recreating the debounced function
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const unsavedValueRef = useRef<string | null>(null);
  const shownContentRef = useRef(content);

  const saveValue = useCallback((value: string) => {
    const save = saveAndRecord(value);
    latestSaveRef.current = save;
    return save;
  }, []);

  const saveAndRecord = async (value: string): Promise<boolean> => {
    if (!onUpdateRef.current) return true;
    const update = onUpdateRef.current;
    const saved = await Promise.resolve()
      .then(() => update(value))
      .then(
        () => true,
        () => false,
      );
    if (saved) savedUploadIdsRef.current = new Set(extractImageUploadIds(JSON.parse(value)));
    if (!saved || unsavedValueRef.current !== value) return saved;
    unsavedValueRef.current = null;
    setLastSavedAt(new Date());
    setHasPendingChanges(false);
    return true;
  };

  const debouncedOnUpdate = useMemo(() => {
    // When debounceWait is 0, call instantly without debouncing
    if (debounceWait === 0) {
      const noop = () => undefined;
      return Object.assign(saveValue, { flush: noop, cancel: noop });
    }

    return debounce(saveValue, debounceWait, { maxWait: debounceMaxWait });
  }, [saveValue, debounceWait, debounceMaxWait]);

  const saveNow = () => {
    debouncedOnUpdate.cancel();
    if (unsavedValueRef.current !== null) void saveValue(unsavedValueRef.current);
  };

  // Update label periodically
  useEffect(() => {
    if (!lastSavedAt) {
      setLastSavedLabel(null);
      return;
    }

    const updateLabel = () => {
      setLastSavedLabel(getLastSavedLabel({ lastSavedAt, t }));
    };

    updateLabel();
    const interval = setInterval(updateLabel, 30000);

    return () => clearInterval(interval);
  }, [lastSavedAt, t]);

  // Flush on beforeunload (tab close/refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      debouncedOnUpdate.flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [debouncedOnUpdate]);

  // Flush on unmount (navigation within app)
  useEffect(() => {
    return () => {
      debouncedOnUpdate.flush();
    };
  }, [debouncedOnUpdate]);
  const [resolvedDocUrls, setResolvedDocUrls] = useState<Record<string, string>>({});
  const [failedDocIds, setFailedDocIds] = useState<Set<string>>(new Set());
  const objectUrlCache = useRef<Record<string, string>>({});
  const inFlightDocIds = useRef<Set<string>>(new Set());
  const suppressOnUpdate = useRef<boolean>(false);

  const handleFileUpload = useCallback<HandleFileUpload>(
    ({ editor, files, position }) => {
      if (!createPostRequest) {
        files.forEach((file) => {
          insertImageFromReader({ editor, file, position });
        });
        return;
      }

      files.forEach((file) => {
        const uploadId = uuid();
        const previewSrc = URL.createObjectURL(file);

        const insertAndUpload = async () => {
          const dimensions = await getImageDimensions(previewSrc);

          if (textDocumentId) {
            setImageStatus(uploadId, { state: "uploading", progress: 0 });
            insertImageNode({ editor, src: previewSrc, uploadId, position, width: dimensions?.width, height: dimensions?.height });
            try {
              const upload = await createPostRequest({ uploadId, file });
              await uploadImageToS3({
                upload,
                file,
                onProgress: (pct) => setImageStatus(uploadId, { state: "uploading", progress: pct }),
              });
              setImageStatus(uploadId, { state: "processing" });
            } catch (error) {
              setImageStatus(uploadId, { state: "failed" });
              toast({ status: "error", title: t("Couldn't upload image. Please try again.") });
            }
          } else {
            insertImageNode({ editor, src: previewSrc, uploadId, position, width: dimensions?.width, height: dimensions?.height });
            try {
              const upload = await createPostRequest({ uploadId, file });
              await uploadImageToS3({ upload, file });
            } catch (error) {
              removeImageNode({ editor, uploadId });
              toast({ status: "error", title: t("Couldn't upload image. Please try again.") });
              console.error(error); // eslint-disable-line no-console
            }
          }
        };

        insertAndUpload();
      });
    },
    [createPostRequest, textDocumentId, setImageStatus, toast, t]
  );

  const handleCopyTemplateDocuments = useCallback<TipTapCopyTemplateDocumentsHandler>(
    async ({ templateDocuments }) => {
      if (!createPostRequest) return { uploadIdMapping: {}, srcMapping: {} };

      const uploadIdMapping: Record<string, string> = {};
      const srcMapping: Record<string, string> = {};

      await Promise.all(
        templateDocuments.map(async (doc) => {
          if (!doc.fromUploadId) return;

          try {
            // Download the image
            const downloadUrl = await getDownloadLink(doc.id, false);
            if (!downloadUrl) throw new Error("Failed to get download link");

            const response = await fetch(downloadUrl);
            if (!response.ok) throw new Error("Failed to download image");

            const blob = await response.blob();
            const file = new File([blob], doc.name, { type: doc.mimeType });

            // Create blob URL for immediate preview
            const previewUrl = URL.createObjectURL(blob);
            srcMapping[doc.fromUploadId] = previewUrl;

            // Upload to new text document
            const newUploadId = uuid();
            const upload = await createPostRequest({ uploadId: newUploadId, file });
            await uploadImageToS3({ upload, file });

            // Map old uploadId to new uploadId
            uploadIdMapping[doc.fromUploadId] = newUploadId;
          } catch (error) {
            console.error(`Failed to copy document ${doc.id}:`, error); // eslint-disable-line no-console
          }
        })
      );

      return { uploadIdMapping, srcMapping };
    },
    [createPostRequest, getDownloadLink]
  );

  const editorExtensions = useMemo(
    () => [...createTipTapExtensions(handleFileUpload), Placeholder.configure({ placeholder: () => placeholderRef.current })],
    [handleFileUpload],
  );

  const editor = useEditor({
    extensions: editorExtensions,
    content: (parseTiptapContent(content) ?? "") as Content,
    editable,
    editorProps: {
      attributes: editable
        ? {
            role: "textbox",
            "aria-multiline": "true",
            "aria-describedby": keyboardHintId,
            ...editableFieldName(ariaLabel, formControl?.labelId),
          }
        : { role: "presentation" },
      // Alt+F10 is the conventional way into an editor toolbar. It is the only reliable one here:
      // TipTap binds Tab inside lists and tables, so Tab does not necessarily leave the text.
      handleKeyDown: (_view, event) => {
        if (!isToolbarShortcut(event)) return false;
        event.preventDefault();
        menuBarRef.current?.focus();
        return true;
      },
      handleDOMEvents: {
        paste: (_view, event) => {
          incomingImageFileRef.current = carriesUploadableImage(event.clipboardData);
          return false;
        },
        drop: (_view, event) => {
          incomingImageFileRef.current = carriesUploadableImage(event.dataTransfer);
          return false;
        },
      },
      transformPastedHTML: (html) => {
        const { html: kept, removedCount, removedFromLocalFilesCount } = stripExternalImages(html, ownImageOrigins());
        const imageFileIsUploading = incomingImageFileRef.current;
        incomingImageFileRef.current = false;
        if (removedCount > 0 && !imageFileIsUploading) {
          const fromDocument = removedFromLocalFilesCount === removedCount;
          toast({
            status: "warning",
            title: fromDocument
              ? t("The images from the document weren't pasted")
              : t("Images from other websites can't be pasted"),
            description: fromDocument
              ? t("Save the images as files, then drag them into the text.")
              : t("Save the image to your computer, then drag the file into the text or paste it."),
            duration: 8000,
            isClosable: true,
          });
        }
        return kept;
      },
    },
    onCreate: ({ editor }) => {
      previousUploadIdsRef.current = new Set(extractImageUploadIds(editor.getJSON()));
      savedUploadIdsRef.current = new Set(previousUploadIdsRef.current);
      onEditorReady?.(editor);
    },
    onUpdate: ({ editor }) => {
      if (suppressOnUpdate.current) return;

      const currentUploadIds = new Set(extractImageUploadIds(editor.getJSON()));
      previousUploadIdsRef.current.forEach((uploadId) => {
        if (!currentUploadIds.has(uploadId)) removedUploadIdsRef.current.add(uploadId);
      });
      currentUploadIds.forEach((uploadId) => removedUploadIdsRef.current.delete(uploadId));
      previousUploadIdsRef.current = currentUploadIds;

      const value = JSON.stringify(editor.getJSON());
      unsavedValueRef.current = value;
      shownContentRef.current = value;
      setHasPendingChanges(true);
      debouncedOnUpdate(value);
    },
    onFocus: () => {
      setEditorFocused(true);
      setEnteredByKeyboard(isKeyboardModality());
    },
    onBlur: () => {
      debouncedOnUpdate.flush();
      setEditorFocused(false);
    },
  }, [editable]);

  useEffect(() => {
    // TrailingNode appends its paragraph after a closing image on the first transaction, which undo would otherwise take back.
    if (editor && !editor.isDestroyed) editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
  }, [editor, placeholder]);

  const saveLatestRef = useRef<() => Promise<boolean>>(async () => true);
  saveLatestRef.current = async () => {
    debouncedOnUpdate.flush();
    await latestSaveRef.current;
    if (unsavedValueRef.current === null) return true;
    debouncedOnUpdate.cancel();
    return saveValue(unsavedValueRef.current);
  };

  const removeDocumentsOfRemovedImagesRef = useRef<() => Promise<void>>(async () => undefined);
  removeDocumentsOfRemovedImagesRef.current = async () => {
    clearUndoHistory(editor);
    debouncedOnUpdate.flush();
    const removed = [...removedUploadIdsRef.current];
    removedUploadIdsRef.current.clear();
    await (removed.length > 0 ? saveLatestRef.current() : latestSaveRef.current);
    const { onDocumentRemove: remove, documents: known } = removeDocumentRef.current;
    for (const uploadId of removed) {
      if (savedUploadIdsRef.current.has(uploadId)) {
        if (!previousUploadIdsRef.current.has(uploadId)) removedUploadIdsRef.current.add(uploadId);
        continue;
      }
      const document = known?.find((doc) => doc.fromUploadId === uploadId);
      if (document && remove) await Promise.resolve(remove({ documentId: document.id })).catch(() => undefined);
    }
  };

  const pendingSaves = usePendingSaves();
  useEffect(() => {
    const leaving = {
      flush: savesOnFlush ? flushOrRefuse : () => undefined,
      leave: () => removeDocumentsOfRemovedImagesRef.current(),
    };
    pendingSaves?.add(leaving);
    return () => {
      pendingSaves?.delete(leaving);
    };

    async function flushOrRefuse() {
      if (!(await saveLatestRef.current())) throw new UnsavedChangesError("saveFailed", true);
    }
  }, [pendingSaves, savesOnFlush]);

  useEffect(() => {
    const removeDocumentsOfRemovedImages = () => void removeDocumentsOfRemovedImagesRef.current();
    window.addEventListener("pagehide", removeDocumentsOfRemovedImages);
    return () => {
      window.removeEventListener("pagehide", removeDocumentsOfRemovedImages);
      removeDocumentsOfRemovedImages();
    };
  }, []);

  const labelId = formControl?.labelId;
  useEffect(() => {
    if (!editable || !editor || editor.isDestroyed) return;
    editor.setOptions({
      editorProps: {
        ...editor.options.editorProps,
        attributes: {
          role: "textbox",
          "aria-multiline": "true",
          "aria-describedby": keyboardHintId,
          ...editableFieldName(ariaLabel, labelId),
        },
      },
    });
  }, [ariaLabel, editable, editor, keyboardHintId, labelId]);

  useEffect(() => {
    if (!editable && editor && !editor.isDestroyed) {
      editor.commands.setContent((parseTiptapContent(content) ?? "") as Content, { emitUpdate: false });
    }
  }, [content, editable, editor]);

  useEffect(() => {
    if (!editable || !editor || editor.isDestroyed || content === shownContentRef.current) return;
    if (unsavedValueRef.current !== null || editor.isFocused) return;
    shownContentRef.current = content;
    editor
      .chain()
      .setMeta("addToHistory", false)
      .setContent((parseTiptapContent(content) ?? "") as Content, { emitUpdate: false })
      .run();
  }, [content, editable, editor]);

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  useEffect(() => {
    if (!documents || documents.length === 0) return;

    const seenIds = new Set<string>();
    const uniqueDocuments = documents.filter((doc) => {
      if (seenIds.has(doc.id)) return false;
      seenIds.add(doc.id);
      return true;
    });

    const toFetchIds = new Set<string>();
    const pendingDocs = uniqueDocuments.filter((doc) => {
      const alreadyResolved = resolvedDocUrls[doc.id] !== undefined;
      const alreadyFailed = failedDocIds.has(doc.id);
      const inFlight = inFlightDocIds.current.has(doc.id);
      if (alreadyResolved || alreadyFailed || inFlight) return false;
      toFetchIds.add(doc.id);
      return true;
    });
    if (pendingDocs.length === 0) return;

    toFetchIds.forEach((id) => inFlightDocIds.current.add(id));

    const resolve = async () => {
      const successes: { doc: TipTapDocument; objectUrl: string }[] = [];
      const failures: string[] = [];

      await Promise.all(
        pendingDocs.map(async (doc) => {
          try {
            const url = await getDownloadLink(doc.id, false);
            if (!url) throw new Error("missing download url");
            const response = await fetch(url);
            if (!response.ok) throw new Error(`download failed ${response.status}`);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            successes.push({ doc, objectUrl });
          } catch (error) {
            failures.push(doc.id);
            console.error(error); // eslint-disable-line no-console
          }
        })
      );

      if (successes.length > 0) {
        setResolvedDocUrls((current) => {
          const next = { ...current };
          successes.forEach(({ doc, objectUrl }) => {
            const existing = current[doc.id];
            if (existing && existing !== objectUrl) {
              URL.revokeObjectURL(existing);
            }
            objectUrlCache.current[doc.id] = objectUrl;
            next[doc.id] = objectUrl;
          });
          return next;
        });
      }

      if (failures.length > 0) {
        setFailedDocIds((current) => new Set([...Array.from(current), ...failures]));
      }

      toFetchIds.forEach((id) => inFlightDocIds.current.delete(id));
    };

    void resolve();
  }, [documents, failedDocIds, getDownloadLink, resolvedDocUrls]);

  useEffect(() => {
    const cacheSnapshot = objectUrlCache.current;
    return () => {
      Object.values(cacheSnapshot).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (!editor || !documents || documents.length === 0) return;

    suppressOnUpdate.current = true;

    const existingSources = new Set<string>();
    const existingUploadIds = new Set<string>();
    editor.state.doc.descendants((node) => {
      if (node.type.name === "image" && typeof node.attrs.src === "string") {
        existingSources.add(node.attrs.src as string);
        if (typeof node.attrs.uploadId === "string") {
          existingUploadIds.add(node.attrs.uploadId);
        }
      }
    });

    documents.forEach((doc) => {
      const src = resolvedDocUrls[doc.id];
      if (!src) return;
      if (!doc.fromUploadId) return;

      if (existingUploadIds.has(doc.fromUploadId)) {
        updateImageNodeByUploadId({ editor, uploadId: doc.fromUploadId, src });
      }
    });

    suppressOnUpdate.current = false;
  }, [documents, editor, resolvedDocUrls]);

  const showSaveStatus = editable && onUpdate && debounceWait !== 0;
  const hintState = { toolbarFocused, editorFocused, enteredByKeyboard };
  const showKeyboardHint = editable && hasEditorKeyboardHint(hintState);

  return (
    <Flex
      flexDirection="column"
      border={hasBorder ? "1px solid" : ""}
      borderColor={editable ? "smInputBorder" : "smBorder"}
      rounded="md"
      width="full"
      maxHeight={maxHeight}
    >
      {editor && (
        <>
          {editable ? (
            <Flex
              roundedTop="md"
              backgroundColor="smBackgroundSecondary"
              shadow="sm"
              {...focusWithinHandlers({ onChange: setToolbarFocused })}
            >
              <MenuBar
                ref={menuBarRef}
                editor={editor}
                onImageSelect={createPostRequest ? (files) => handleFileUpload({ editor, files }) : undefined}
                onCopyTemplateDocuments={createPostRequest ? handleCopyTemplateDocuments : undefined}
                onAutofill={onAutofill}
                isOpeningAutofill={isOpeningAutofill}
                onTemplateInserted={() => debouncedOnUpdate.flush()}
                askFurtherAbout={askFurtherAbout}
                toolbar={toolbar}
              />
            </Flex>
          ) : null}
          {editable && (
            /* Announced when focus enters the text; the sighted equivalent is EditorKeyboardHint. */
            <VisuallyHidden id={keyboardHintId}>
              {t("Press {{shortcut}} for the formatting toolbar", { shortcut: toolbarShortcut() })}
            </VisuallyHidden>
          )}
          <Flex
            flexDirection="column"
            grow={1}
            minWidth="full"
            width="full"
            maxWidth="full"
            height="full"
            maxHeight="full"
            overflowX="auto"
            overflowY="auto"
            p={padding}
            ref={readOnlyScroll.ref}
            tabIndex={readOnlyScroll.tabIndex}
            _focusVisible={KEYBOARD_SCROLL_FOCUS}
          >
            <Box>
              {textDocumentId ? (
                <UploadStatusProvider textDocumentId={textDocumentId} statusByUploadId={statusByUploadId}>
                  <EditorContent editor={editor} />
                </UploadStatusProvider>
              ) : (
                <EditorContent editor={editor} />
              )}
            </Box>
          </Flex>
          {(showSaveStatus || showKeyboardHint) && (
            <Flex
              justifyContent="flex-end"
              alignItems="center"
              gap="2"
              px="2"
              py="1"
              minH="8"
              borderTop="1px solid"
              borderColor="smBorder"
              backgroundColor="smBackgroundSecondary"
              roundedBottom="md"
            >
              {showKeyboardHint && <EditorKeyboardHint {...hintState} />}
              {showSaveStatus && (
                <>
                  <Text fontSize="xs" color="smMuted">
                    {hasPendingChanges ? t("Unsaved changes") : (lastSavedLabel ?? t("No changes"))}
                  </Text>
                  {hasPendingChanges && (
                    <Button size="xs" colorScheme="teal" variant="ghost" onClick={saveNow}>
                      {t("Save now")}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};

const editableFieldName = (ariaLabel: string | undefined, labelId: string | undefined): Record<string, string> => {
  if (ariaLabel) return { "aria-label": ariaLabel };
  if (labelId) return { "aria-labelledby": labelId };
  return {};
};

export const clearUndoHistory = (editor: Editor | null) => {
  if (!editor || editor.isDestroyed) return;
  const { doc, selection, plugins } = editor.state;
  editor.view.updateState(EditorState.create({ doc, selection, plugins }));
  editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
};
